import { useCallback } from 'react';
import { axiosPrivate } from '@/api/axios';
import { resolveCompanyHierarchy } from '@/lib/companyHierarchy';
import { CONTAINER_URL, USER_API_PATH } from '@/lib/constants';
import type { AuthUser, BusinessConfig, WhiteLabel } from '@/types/auth';

/**
 * Hook that fetches the full current user profile after login/refresh.
 * Replicates AngularJS login.service.ts getCurrent() (lines 163-310).
 *
 * Uses axiosPrivate directly (cookie-authenticated) to avoid
 * circular dependency with useApi → useAuth.
 *
 * Flow:
 * 1. GET /Companies/current with include filters
 * 2. Apply resolveCompanyHierarchy()
 * 3. Fetch businessConfig (BUSINESS_CONFIG configItem)
 * 4. Fetch whiteLabel (WHITELABEL configItem)
 * 5. Calculate demo/payment days
 * 6. Return fully hydrated AuthUser
 */
export function useGetCurrent() {
  const getCurrent = useCallback(async (): Promise<AuthUser> => {
    // 1. Fetch full user profile with relations
    const filter = {
      include: [
        'employee',
        'mmxrole',
        {
          relation: 'masterCompany',
          scope: {
            fields: { id: true, businessTypes: true },
          },
        },
        'statusList',
        {
          relation: 'company',
          scope: {
            include: [
              'statusList',
              {
                relation: 'masterCompany',
                scope: {
                  fields: { id: true, businessTypes: true },
                },
              },
            ],
          },
        },
      ],
    };

    const response = await axiosPrivate.get(
      `/${USER_API_PATH}/current?filter=${encodeURIComponent(JSON.stringify(filter))}`
    );
    const user: AuthUser = response.data;

    // 2. Resolve company hierarchy (Matriz/Hijo/Sucursal)
    const resolved = resolveCompanyHierarchy(user);

    // 3. Fetch businessConfig
    const companyId = resolved.companyId!;
    try {
      const configFilter = { where: { configType: 'BUSINESS_CONFIG' } };
      const configResponse = await axiosPrivate.get(
        `/${USER_API_PATH}/${companyId}/configItems?filter=${encodeURIComponent(JSON.stringify(configFilter))}`
      );
      const configItems = configResponse.data;
      if (Array.isArray(configItems) && configItems.length > 0) {
        resolved.businessConfig = configItems[0].data as BusinessConfig;
        resolved.businessConfigId = configItems[0].id;
      } else {
        resolved.businessConfig = null;
        resolved.businessConfigId = null;
      }
    } catch {
      resolved.businessConfig = null;
      resolved.businessConfigId = null;
    }

    // 4. Fetch whiteLabel
    try {
      const wlFilter = { where: { configType: 'WHITELABEL' } };
      const wlResponse = await axiosPrivate.get(
        `/${USER_API_PATH}/${companyId}/configItems?filter=${encodeURIComponent(JSON.stringify(wlFilter))}`
      );
      const wlItems = wlResponse.data;
      if (Array.isArray(wlItems) && wlItems.length > 0) {
        resolved.whiteLabel = wlItems[0] as WhiteLabel;
      }
    } catch {
      // whiteLabel is optional
    }

    // 5. Calculate demo days and payment days
    const createdDate = new Date(resolved.created);
    const demoDays = resolved.demoDays ?? 0;
    const demoEndDate = new Date(createdDate);
    demoEndDate.setDate(demoEndDate.getDate() + demoDays);
    const now = new Date();

    resolved.lastDaysForDemo = Math.floor(
      (demoEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)
    );
    resolved.daysInApp = Math.floor(
      (now.getTime() - createdDate.getTime()) / (1000 * 60 * 60 * 24)
    );

    if (resolved.nextPayment) {
      resolved.daysForPayment = Math.floor(
        (now.getTime() - new Date(resolved.nextPayment).getTime()) / (1000 * 60 * 60 * 24)
      );
    }

    // 6. Set logo
    resolved.logo = resolved.image
      ? `${CONTAINER_URL}/${resolved.image}`
      : '/assets/images/logo_image.png';

    return resolved;
  }, []);

  return getCurrent;
}
